/**
 * Distinguishes a sound pulled for CONTENT MODERATION from every other kind of
 * deactivated row.
 *
 * `is_active = false` is overloaded. It covers dedup merges, archived rows and
 * broken audio — ~1,700 rows — and TES-576 deliberately keeps those reachable
 * at their own URL (noindexed) so links people already have still resolve. That
 * is the right call for a merged duplicate.
 *
 * It is the wrong call for a row pulled for hate speech, and the two were never
 * separated. TES-1733 found the consequence in production: `/sounds/neegy`, the
 * TES-1618 racial-slur incident, was still returning HTTP 200 and publishing
 * `og:audio` pointing at the slur — months after being "removed". Deactivation
 * had stopped it appearing in listings, but the page, the player and the file
 * were all still served. The same was true of the row this back-screen found.
 *
 * The signal is `deactivated_reason`. Two different prefixes have been used to
 * record a content pull, and BOTH must block:
 *
 *   - `content_moderation…`               the code path (TES-1731/TES-1733,
 *                                         and TES-1618 by hand)
 *   - `curation: offensive/hate content…` a hand-written 2026-06-29 sweep
 *
 * The second form was missed on the first pass of this fix, which left five
 * rows — "1488", "Chimpin Out Chud", "Detector De Marica", "Nworldeiqwjhfsa",
 * "Wiggers" — serving HTTP 200 with `og:audio` after the other two were
 * already blocked. They are the same class of content as the row this module
 * was written for; only the wording of the reason differed.
 *
 * Prefixes are matched at the START of the reason, never as a substring. Two
 * production dedup rows cite `lib/content-moderation-rules.ts` by path in
 * their reason, and a substring match would wrongly 404 both.
 *
 * When adding a moderation path, prefer writing the `content_moderation`
 * prefix over inventing a third wording — this list is the enforcement point,
 * and a reason it does not recognise silently keeps the row served.
 */
export interface ContentBlockCandidate {
  is_active?: boolean | null
  deactivated_reason?: string | null
}

/** Reason prefixes that mark a row as pulled for its content. */
const MODERATION_REASON_PREFIXES = [
  'content_moderation',
  'curation: offensive/hate content',
] as const

export function isContentBlocked(sound: ContentBlockCandidate | null | undefined): boolean {
  if (!sound) return false
  // An active row is never blocked, whatever its reason column says — a row
  // that was pulled and later reinstated must not stay 404 forever.
  if (sound.is_active !== false) return false
  const reason = (sound.deactivated_reason ?? '').trimStart().toLowerCase()
  return MODERATION_REASON_PREFIXES.some((prefix) => reason.startsWith(prefix))
}
